/**
 * A pane over the map, on a phone.
 *
 * The map stays mounted and visible behind it — a pilot reading the NavLog is
 * still reading it against the route — and the sheet uses the same three
 * detents and the same drag arithmetic as the map's own route sheet
 * (`map/sheet.ts`), because two sheets in one app that snap differently is two
 * sheets too many. Pulling it all the way down goes back to the map.
 */
import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useT } from '../i18n';
import { SHEET_MIN_PX, cycleSheet, snapSheet, type SheetState } from '../map/sheet';

export function PaneSheet({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose(): void;
}) {
  const t = useT();
  const [state, setState] = useState<SheetState>('full');
  /** live height while the grab bar is dragged; null when it is not */
  const [dragH, setDragH] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const onGrab = (e: ReactPointerEvent<HTMLButtonElement>) => {
    const el = ref.current;
    const box = el?.parentElement;
    if (!el || !box) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const max = box.clientHeight;
    const startY = e.clientY;
    const startH = el.getBoundingClientRect().height;
    const move = (ev: PointerEvent) => {
      const h = Math.max(SHEET_MIN_PX, Math.min(max, startH + (startY - ev.clientY)));
      setDragH(h);
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const h = Math.max(SHEET_MIN_PX, Math.min(max, startH + (startY - ev.clientY)));
      const next = snapSheet(state, h, max, startY - ev.clientY);
      setDragH(null);
      if (next === 'collapsed') onClose();
      else setState(next);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const onGrabClick = () => {
    const next = cycleSheet(state);
    if (next === 'collapsed') onClose();
    else setState(next);
  };

  const height = dragH !== null ? dragH + 'px' : state === 'full' ? '100%' : '50%';

  return (
    <aside
      ref={ref}
      className={'ae-sheet' + (dragH !== null ? ' dragging' : '')}
      style={{ height }}
      aria-label={title}
    >
      <button
        type="button"
        className="ae-sheet-grab"
        title={t('map.sheet.grab')}
        onPointerDown={onGrab}
        onClick={onGrabClick}
      >
        <span>{title}</span>
      </button>
      <div className="ae-sheet-body">{children}</div>
    </aside>
  );
}
